import React, { useEffect } from "react";
import styles from "./selector-interface.module.scss";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBridge } from "@/features/bridge/providers/bridge.provider";
import { useAccount } from "wagmi";
import { useMainnetToTestnetSwitchStore } from "@/stores/mainet-testnet-switcher.store";
import { useNftTokenManager } from "@/features/bridge/stores/nft-token-manager";
import { fetchAllNfts } from "@/features/bridge/services/alchemy/getAllNfts";
import { collectionContains } from "@/features/bridge/utils/object-contains";
import ModalPopper from "@components/layout/modal-popper/modal-popper";
import CollectionCard from "@/features/bridge/components/token-selector/selector-interface/collection-card";
import CardList from "@/features/bridge/components/token-selector/selector-interface/collection-list";
import MultiTokenConfirmation from "@/features/bridge/components/token-confirmation/multi-token-confirmation";
import { useTokenWindowControls } from "@/features/bridge/stores/token-window.store";
import { TokenType } from "@/features/bridge/types/token-selector.types";
import BridgingHeader from "./bridging-header";
import { useTokenFilterStore } from "@/features/bridge/stores/token-filter.store";
import { useCollectionEligibility } from "@/features/bridge/hooks/useCollectionEligibility.hook";
import type { GeneralTokenCollectionType } from "@/features/bridge/types/open-sea-api.types";

export interface Token {
  id: string;
  name: string;
  image: string;
  collection: string;
}

// Component to check collection eligibility and conditionally render
const FilteredCollectionCard = ({
  collectionKey,
  collection,
  ...props
}: {
  collectionKey: string;
  collection: GeneralTokenCollectionType[string];
  collectionName: string;
  setActiveCollection: (collection: string) => void;
  activeTokenCollection: string | undefined;
  collectionAmount: number | undefined;
  selectionCount: number;
  isDisabled: boolean;
  clearTokenCollection: () => void;
  tokenType: "NFT" | "MULTI";
}) => {
  const { showOnlyBridgeableTokens } = useTokenFilterStore();
  const collectionEligibility = useCollectionEligibility(collection);

  // If filtering is enabled and collection has no eligible tokens, don't render
  if (showOnlyBridgeableTokens && !collectionEligibility.hasEligibleTokens) {
    return null;
  }

  return (
    <CollectionCard
      collection={collection}
      collectionKey={collectionKey}
      {...props}
    />
  );
};

const NftSelectionWindow = () => {
  const { activeTokenWindowType, setActiveTokenWindowType } =
    useTokenWindowControls();
  const isOpen = activeTokenWindowType === TokenType.NFT;

  const {
    bridgeControls: {
      fromChain: { originChain },
      toChain: { destinationChain },
    },
  } = useBridge();

  const {
    collections,
    setActiveCollection,
    activeCollection,
    getSelectionCount,
    setCollections,
    setTokensFound,
    deselectAllTokens,
    selectAllTokens,
    getAllSelectionsCount,
    getActiveCollection,
    removeSuccessfullySentTokens,
    clearAndReset,
  } = useNftTokenManager();

  const account = useAccount();

  const queryClient = useQueryClient();

  const { data, status, isFetching, refetch } = useQuery({
    queryKey: [`${originChain?.chainName}-nfts`],
    queryFn: () => fetchAllNfts(account.address, originChain?.chainId),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!account.address && !!originChain?.chainId && isOpen,
  });

  useEffect(() => {
    clearAndReset();
  }, [originChain?.chainId, account.address, clearAndReset, refetch]);

  useEffect(() => {
    if (status === "success" && data) {
      setCollections(data);
      if (collectionContains(data!)) setTokensFound(true);
      else setTokensFound(false);
    }
  }, [
    data,
    status,
    setCollections,
    setTokensFound,
    queryClient,
    originChain?.chainName,
  ]);

  const handleOnClose = () => {
    setActiveTokenWindowType(null);
  };

  return (
    <ModalPopper isOpen={isOpen} onClose={handleOnClose} destroyOnClose>
      <div className={styles.container}>
                <div className={styles.selectTokenHeader}>
          <BridgingHeader
            originChain={originChain}
            destinationChain={destinationChain}
          />
        </div>
        <div className={styles.wrapper}>
          <div className={styles.left}>
            <div className={styles.collections}>
              {Object.entries(collections).map(([collectionKey, collection], index) => {
                const collectionName = collection.collectionData.name;
                const disabled =
                  !getSelectionCount(collectionKey) &&
                  !!getAllSelectionsCount();
                return (
                  <FilteredCollectionCard
                    key={collectionKey}
                    collectionKey={collectionKey}
                    collection={collection}
                    collectionName={collectionName}
                    setActiveCollection={setActiveCollection}
                    activeTokenCollection={activeCollection}
                    collectionAmount={collection.collectionData.amount}
                    selectionCount={getSelectionCount(collectionKey)}
                    isDisabled={disabled}
                    clearTokenCollection={deselectAllTokens}
                    tokenType="NFT"
                  />
                );
              })}
            </div>
          </div>
          <div className={styles.right}>
            <CardList
              isLoading={isFetching}
              collections={collections}
              getSelectionCount={getSelectionCount}
              selectAllTokens={selectAllTokens}
              refetchTokens={refetch}
            />
          </div>
          <div className={styles.checkoutPanel}>
            <MultiTokenConfirmation
              selectedCollection={getActiveCollection()}
              clearTokenSelection={deselectAllTokens}
              removeSentTokens={removeSuccessfullySentTokens}
            />
          </div>
        </div>
      </div>
    </ModalPopper>
  );
};

export default NftSelectionWindow;
